// Single source of truth for "may THIS account use the Gas Tank, and if not, why?".
//
// It exists because the answer has to be identical on two builds that cannot
// share a hook: the extension reads controller state through the `@web/hooks/*`
// background-service hooks, while mobile-web / native read it through
// `useController` (the extension mounts no `ControllerStoreProvider`). Before
// this module the two had drifted badly — the extension still ran the upstream
// Ambire check (account type only), so it happily offered a top-up on accounts
// that could never spend the deposit, which is the exact funds-safety hole
// docs/gas-tank-testing.md §N1–N4 closed on mobile-web.
//
// Keep this pure: no hooks, no controller access — the two `useHasGasTank`
// wrappers feed it the state they already have.
import { AMBIRE_ACCOUNT_FACTORY } from '@ambire-common/consts/deploy'
import { HARDWARE_WALLET_DEVICE_NAMES } from '@ambire-common/consts/hardwareWallets'
import { Account } from '@ambire-common/interfaces/account'
import { Key } from '@ambire-common/interfaces/keystore'
import { Network } from '@ambire-common/interfaces/network'
import { canBecomeSmarter, isSmartAccount } from '@ambire-common/libs/account/account'
import { getIsViewOnly } from '@ambire-common/utils/accounts'
import { getGasTankSpendableNetworks } from '@common/utils/gasTankSupport'
import { FEATURES } from '@web/constants/features'

export type GasTankEligibility = {
  canUseGasTank: boolean
  isViewOnly: boolean
  disabledReason: string
  /**
   * Every network where this account could actually spend the tank. The balance
   * is cross-chain, so the chain a user tops up on is irrelevant — but if this
   * list is empty the account can never spend what it deposits, and the UI must
   * say so instead of taking the money. See `common/utils/gasTankSupport.ts`.
   */
  spendableNetworks: Network[]
}

type Translate = (key: string, options?: Record<string, any>) => string

export const getGasTankEligibility = ({
  account,
  keys,
  networks,
  t
}: {
  account: Account | null
  keys: Key[]
  networks: Network[]
  t: Translate
}): GasTankEligibility => {
  if (!account)
    return { canUseGasTank: false, isViewOnly: false, disabledReason: '', spendableNetworks: [] }

  // Build-time kill-switch (GAS_TANK_ENABLED=false). Every "may I offer the
  // tank?" question on both builds funnels through here, so answering "no" once
  // hides the pill, the top-up entry points and the fee options together.
  if (!FEATURES.gasTankUiEnabled)
    return {
      canUseGasTank: false,
      isViewOnly: getIsViewOnly(keys, account.associatedKeys),
      disabledReason: t('Gas Tank is not available in this build.'),
      spendableNetworks: []
    }

  const accountKeys = keys.filter((key) => account.associatedKeys.includes(key.addr))
  const isViewOnly = getIsViewOnly(keys, account.associatedKeys)

  const spendableNetworks = getGasTankSpendableNetworks(networks, {
    isSmartAccount: isSmartAccount(account),
    canUpgradeToSmart: canBecomeSmarter(account, accountKeys)
  })

  const externalKeyNames = accountKeys
    .map((key) => key.type)
    .filter(
      (type): type is keyof typeof HARDWARE_WALLET_DEVICE_NAMES =>
        type in HARDWARE_WALLET_DEVICE_NAMES
    )
    .map((type) => HARDWARE_WALLET_DEVICE_NAMES[type])

  const canUseGasTank = (() => {
    if (account.safeCreation) return false // not available for Safe accounts

    // Not available for v1 accounts. One could argue whether checking the
    // factoryAddr is the best approach, but the alternative is checking the
    // account state (an on-chain metric), which would make this needlessly
    // harder. Collateral damage might be old v2 SAs used for testing that are
    // already deprecated — chances are the gas tank doesn't work there either.
    if (account.creation && account.creation.factoryAddr !== AMBIRE_ACCOUNT_FACTORY) return false

    // Assume view-only accounts CAN use the Gas Tank, not knowing their key
    // types yet. They cannot sign anything, so nothing is spent on this branch.
    if (isViewOnly) return true

    // NOT gated on key type. `docs/gas-tank-frontend-integration.md` (2026-07-25)
    // records hardware wallets as out of scope, but that reasoning only holds
    // for an *EOA*: it needs an EIP-7702 delegation signature, which no HW
    // device produces — and that case already falls out here for free, because
    // `canBecomeSmarter` fails and `spendableNetworks` ends up empty.
    //
    // A v2 Smart Account never signs a 7702 delegation; it signs EIP-712 typed
    // data, which Ledger/Trezor/QR do produce (`signAccountOp.ts` already wraps
    // that path in `#withHardwareWalletSigningRequest`). The relayer imposes no
    // extra rule: `/submit` stores `signature` + `signer` without verifying
    // either (gas-tank BE `internal/httpapi/submit.go`, `internal/submit/service.go`),
    // and the executor's only acceptance test is an `eth_call` simulation of
    // `execute(txns, sig)` — i.e. the AmbireAccount contract's own `ecrecover`
    // against `privileges[signer]`, which cannot tell a device key from an
    // internal one. Identity registration checks the factory only, never key
    // types. Verified against the backend for Trello #426.
    //
    // ZKX: the account type alone is not enough. An EOA that "can become
    // smarter" still needs a chain with 7702 + a deployed paymaster to spend
    // the tank; with none, a top-up would be unspendable. Gate on the real
    // per-network capability instead of on the account type in isolation.
    return spendableNetworks.length > 0
  })()

  const disabledReason = (() => {
    if (canUseGasTank) return ''

    if (account.safeCreation) return t('Not available for Safe wallets, yet.')

    if (account.creation)
      return t(
        'This feature is no longer available for Ambire v1 accounts and other legacy Ambire smart accounts.'
      )

    const unsupportedKeyTypeNames = externalKeyNames

    // No hardware key involved → the blocker is the network side: this account
    // type has no chain where the tank can be spent yet.
    if (!unsupportedKeyTypeNames.length)
      return t(
        'The Gas Tank is not available for this account yet — there is no supported network where it could pay fees. Smart Accounts can use it today; regular accounts need a network with EIP-7702 support.'
      )

    return t(
      "Not available for {{typesOfKey}} wallets yet. Requires EIP-7702 (that enables EOAs to gain smart account capabilities) which these devices don't support yet.",
      { typesOfKey: unsupportedKeyTypeNames.join(` ${t('and')} `) }
    )
  })()

  return { canUseGasTank, isViewOnly, disabledReason, spendableNetworks }
}
